import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SortIcon from "@mui/icons-material/Sort";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import { PageContainer, PageHeader } from "../components/PageHeader";
import SignageCampaignDialog from "../components/SignageCampaignDialog";
import {
  foyerApi,
  type AssetSummary,
  type MediaLayer,
  type SignageScopeRef,
} from "../http/foyer";
import {
  listSignageScreensIn,
  type SignageScreen,
} from "../utils/signageScreens";
import type { SignageAssignment, SignageCampaign } from "@shared/types/signage";
import { tokens } from "../theme";
import { signageSaveErrorText } from "../utils/signageRefusal";

/**
 * Campaigns - the booking list.
 *
 * The two tabs are the media LAYER, which the model already carries: "Location"
 * is the premise layer (this site's own bookings) and "Company" is the chain's,
 * inherited read-only by every location it owns. That is the same split the
 * media library uses, and the design names it the same way
 * (Platskampanjer / Foretagskampanjer).
 */

export type CampaignScope = "location" | "company";

export interface CampaignRow {
  id: string;
  name: string;
  /** Media filename or a summary when a campaign holds several items. */
  media: string;
  /** `YYYY-MM-DD`, or null for "starts immediately". */
  start: string | null;
  /** `YYYY-MM-DD`, or null for "no end date". */
  end: string | null;
  screenCount: number;
  scope: CampaignScope;
}

export interface SignageCampaignsPageProps {
  rows: CampaignRow[];
  /** Authoring for a whole customer rather than one location. */
  chain?: boolean;
  loading?: boolean;
  onCreate: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

type SortKey = "name" | "media" | "start" | "end" | "screenCount";

function formatDate(value: string | null, fallback: string): string {
  if (!value) return fallback;
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function RowMenu({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setEl(e.currentTarget)}
        aria-label="Campaign actions"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={el}
        open={Boolean(el)}
        onClose={() => setEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setEl(null);
            onEdit();
          }}
        >
          <EditOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setEl(null);
            onDelete();
          }}
          sx={{ color: tokens.error }}
        >
          <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}

export function SignageCampaignsPage({
  rows,
  chain = false,
  loading,
  onCreate,
  onEdit,
  onDelete,
}: SignageCampaignsPageProps) {
  const [scope, setScope] = useState<CampaignScope>(
    chain ? "company" : "location"
  );
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [asc, setAsc] = useState(true);

  const visible = useMemo(() => {
    const subset = rows.filter((r) => r.scope === scope);
    const dir = asc ? 1 : -1;
    return [...subset].sort((a, b) => {
      const x = a[sortKey];
      const y = b[sortKey];
      if (x === null) return 1;
      if (y === null) return -1;
      if (typeof x === "number" && typeof y === "number") return (x - y) * dir;
      return String(x).localeCompare(String(y)) * dir;
    });
  }, [rows, scope, sortKey, asc]);

  const head = (key: SortKey, label: string) => (
    <TableCell sortDirection={sortKey === key ? (asc ? "asc" : "desc") : false}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key && !asc ? "desc" : "asc"}
        onClick={() => {
          if (sortKey === key) setAsc((v) => !v);
          else {
            setSortKey(key);
            setAsc(true);
          }
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Signage"
        title="Campaigns"
        subtitle="Create, manage and follow up on your campaigns."
        actions={
          <Stack direction="row" gap={1}>
            <Button variant="outlined" size="small" startIcon={<SortIcon />}>
              Sort
            </Button>
            <Button
              variant="contained"
              size="small"
              endIcon={<AddIcon />}
              onClick={onCreate}
            >
              New campaign
            </Button>
          </Stack>
        }
      />

      {!chain && (
        <Box sx={{ borderBottom: `1px solid ${tokens.divider}`, mb: 2 }}>
          <Tabs value={scope} onChange={(_, v) => setScope(v)}>
            <Tab value="location" label="Location campaigns" />
            <Tab value="company" label="Company campaigns" />
          </Tabs>
        </Box>
      )}

      <Box
        sx={{
          border: `1px solid ${tokens.divider}`,
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: tokens.surface,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {head("name", "Name")}
              {head("media", "Media")}
              {head("start", "Start")}
              {head("end", "End")}
              {head("screenCount", "Screens")}
              <TableCell width={56} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <Typography
                    sx={{ fontSize: 14, fontWeight: 700, color: tokens.ink }}
                  >
                    {r.name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ color: tokens.inkMuted }}>{r.media}</TableCell>
                <TableCell sx={{ color: tokens.inkMuted }}>
                  {formatDate(r.start, "Immediately")}
                </TableCell>
                <TableCell sx={{ color: tokens.inkMuted }}>
                  {/* "No end date" is a real, common state - a campaign that
                      runs until someone stops it. Rendering it as an empty cell
                      would read as missing data. */}
                  {formatDate(r.end, "No end date")}
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <TvOutlinedIcon
                      sx={{ fontSize: 18, color: tokens.inkFaint }}
                    />
                    <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
                      {r.screenCount}{" "}
                      {r.screenCount === 1 ? "screen" : "screens"}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <RowMenu
                    onEdit={() => onEdit(r.id)}
                    onDelete={() => onDelete(r.id)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Stack alignItems="center" sx={{ py: 5 }} gap={0.5}>
                    {loading && (
                      <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                        Loading…
                      </Typography>
                    )}
                    <Typography sx={{ fontWeight: 600, color: tokens.ink }}>
                      {scope === "company"
                        ? "No company campaigns"
                        : "No campaigns at this location"}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
                      {scope === "company"
                        ? "Campaigns booked by the chain appear here for every location it owns."
                        : "Book a campaign to interrupt the background loop on a schedule."}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {visible.length > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, borderTop: `1px solid ${tokens.divider}` }}
          >
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
              Showing {visible.length} of {visible.length} campaigns
            </Typography>
          </Stack>
        )}
      </Box>
    </PageContainer>
  );
}

/* -------------------------------------------------------------------------- */

/** Wire shape -> table row. */
function toRow(c: SignageCampaign): CampaignRow {
  return {
    id: c.id,
    name: c.name,
    media:
      c.items.length === 0
        ? "No media"
        : c.items.length === 1
          ? (c.items[0]!.assetUrl.split("/").pop() ?? "1 item")
          : `${c.items.length} items`,
    start: c.startDate,
    end: c.endDate,
    // Bookings-per-screen is not stored on the campaign; it is derived from how
    // many screens list it. Counted from the assignments rather than guessed.
    screenCount: 0,
    scope: c.scope === "premise" ? "location" : "company",
  };
}

/**
 * Campaigns, wired.
 *
 * Both layers arrive in one call - the API already returns a location's own
 * bookings plus the ones its customer shares - so the two tabs are a filter
 * over one list rather than two round trips.
 */
export default function SignageCampaignsContainer({
  premiseId,
  companyId,
}: {
  /** Exactly one is set: a location, or the whole customer. */
  premiseId?: string;
  companyId?: string;
}) {
  const scope: SignageScopeRef = premiseId
    ? { kind: "premise", premiseId }
    : { kind: "company", companyId: companyId ?? "" };
  const scopeKey = premiseId ?? companyId ?? "";
  const chain = !premiseId;

  const [campaigns, setCampaigns] = useState<SignageCampaign[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [screens, setScreens] = useState<SignageScreen[]>([]);
  const [assets, setAssets] = useState<AssetSummary[]>([]);
  /** Every screen assignment in scope, so the dialog knows what is booked. */
  const [bookings, setBookings] = useState<SignageAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SignageCampaign | null | undefined>(
    undefined
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    foyerApi
      .listSignageCampaignsIn(scope)
      .then((list) => alive && setCampaigns(list))
      .catch(() => alive && setCampaigns([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey, tick]);

  // How many screens have actually booked each campaign. Only meaningful for a
  // single location - the chain view has no one premise to count against.
  useEffect(() => {
    if (!premiseId) return;
    let alive = true;
    foyerApi
      .listSignageAssignments(premiseId)
      .then((rows) => {
        if (!alive) return;
        const tally: Record<string, number> = {};
        for (const r of rows) {
          for (const id of r.campaignIds ?? []) {
            tally[id] = (tally[id] ?? 0) + 1;
          }
        }
        setCounts(tally);
      })
      .catch(() => alive && setCounts({}));
    return () => {
      alive = false;
    };
  }, [premiseId, tick]);

  useEffect(() => {
    let alive = true;
    listSignageScreensIn(scope)
      .then((list) => alive && setScreens(list))
      .catch(() => alive && setScreens([]));
    const layer: MediaLayer = premiseId
      ? { kind: "premise", premiseId }
      : { kind: "company", companyId: companyId ?? "" };
    foyerApi
      .listAssets(layer)
      .then((list) => alive && setAssets(list))
      .catch(() => alive && setAssets([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey, tick]);

  /*
   * Which screens have booked which campaign.
   *
   * Read across every location in scope, not just one - a chain-level campaign
   * is booked by individual screens spread over several of its locations, and
   * showing only one location's would make the others look unbooked.
   */
  useEffect(() => {
    let alive = true;
    const ids = [...new Set(screens.map((s) => s.premiseId))];
    Promise.all(
      ids.map((id) => foyerApi.listSignageAssignments(id).catch(() => []))
    ).then((rows) => alive && setBookings(rows.flat()));
    return () => {
      alive = false;
    };
  }, [screens, tick]);

  const rows = useMemo(
    () =>
      campaigns.map((c) => ({
        ...toRow(c),
        screenCount: counts[c.id] ?? 0,
      })),
    [campaigns, counts]
  );

  const bookedForEditing = useMemo(
    () =>
      editing
        ? bookings
            .filter((b) => (b.campaignIds ?? []).includes(editing.id))
            .map((b) => b.screenId)
        : [],
    [editing, bookings]
  );

  const save = async (patch: Partial<SignageCampaign>, screenIds: string[]) => {
    setSaving(true);
    setError(null);
    try {
      // The campaign first: its id is what the bookings point at, so a failure
      // here must not leave screens referring to something that was never
      // created.
      const saved = editing
        ? await foyerApi.updateSignageCampaignIn(scope, editing.id, patch)
        : await foyerApi.createSignageCampaignIn(scope, patch);
      await bookScreens(saved.id, screenIds);
      setEditing(undefined);
      setTick((t) => t + 1);
    } catch (e) {
      setError(signageSaveErrorText(e, "Could not save the campaign."));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Add the campaign to the screens that should run it, remove it from the
   * rest.
   *
   * Only screens whose booking actually CHANGES are written. Rewriting every
   * screen would touch rows nobody edited, and each write is a real PUT against
   * a location that may be offline or slow.
   */
  const bookScreens = async (campaignId: string, screenIds: string[]) => {
    const byScreen = new Map(bookings.map((b) => [b.screenId, b]));
    const writes = screens.flatMap((s) => {
      const current = byScreen.get(s.id)?.campaignIds ?? [];
      const has = current.includes(campaignId);
      const want = screenIds.includes(s.id);
      if (has === want) return [];
      const next = want
        ? [...current, campaignId]
        : current.filter((id) => id !== campaignId);
      return [
        foyerApi.putSignageAssignment(s.premiseId, s.id, {
          campaignIds: next,
        }),
      ];
    });
    await Promise.all(writes);
  };

  const remove = async (id: string) => {
    const previous = campaigns;
    setCampaigns((prev) => prev.filter((c) => c.id !== id));
    try {
      await foyerApi.deleteSignageCampaignIn(scope, id);
      setTick((t) => t + 1);
    } catch {
      // Put it back rather than leave the table lying about what exists.
      setCampaigns(previous);
    }
  };

  return (
    <>
      <SignageCampaignsPage
        rows={rows}
        chain={chain}
        loading={loading}
        onCreate={() => setEditing(null)}
        onEdit={(id) => setEditing(campaigns.find((c) => c.id === id) ?? null)}
        onDelete={remove}
      />
      <SignageCampaignDialog
        open={editing !== undefined}
        campaign={editing}
        screens={screens}
        bookedScreenIds={bookedForEditing}
        assets={assets}
        onClose={() => setEditing(undefined)}
        onSave={save}
        saving={saving}
        error={error}
      />
    </>
  );
}
